import { Box, Stack } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import {
    PoolBanner,
    PoolButton,
    PoolModal,
    PoolModalActions,
    PoolModalContent,
    PoolModalTitle,
} from '@synergy/components-next';
import { useAppDispatch } from '@synergy/admin/app-settings/data-access';
import { useFormikContext } from 'formik';
import { useRef, useState } from 'react';

import { InvoiceProductItem } from '../../../../models/invoice-product-item';
import { PositionsFormValues, positionFieldNames } from '../../../models/positions-form-values';
import {
    TaxRateAutocomplete,
    TaxRateAutocompleteRef,
} from '../../../../../../common/components/tax-rate-autocomplete/tax-rate-autocomplete';
import { allTaxRatesSelector } from '../../../../../configuration/finance/tax-rates/store/tax-rates-selector';
import { changeRecipientTaxRateId } from '../../../store/invoice-detail-actions';
import { invoiceDetailRecipientTaxRateSelector } from '../../../store/positions/invoice-detail-positions-selectors';
import { isInvoiceGenericItem, isInvoiceProductItem } from '../../../../models/invoice-item';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';
import { useInvoiceDetailContext } from '../../../../context/invoice-detail-context';

export const TaxRateOverrideModal = () => {
    const [showBanner, setShowBanner] = useState(true);
    const taxRates = useAppSelector(allTaxRatesSelector);
    const taxRatesRef = useRef<TaxRateAutocompleteRef | null>(null);
    const formikContext = useFormikContext<PositionsFormValues>();
    const recipientTaxRateId = useAppSelector(invoiceDetailRecipientTaxRateSelector);
    const dispatch = useAppDispatch();
    const { setShowTaxRateOverrideModal, showTaxRateOverrideModal, setTaxRateForOverride } = useInvoiceDetailContext();

    const handleSetAllTaxRates = () => {
        // We need to get the taxRate from the select, because we also need the taxPercentage
        const taxRate = taxRatesRef?.current?.getTaxRate(recipientTaxRateId);
        if (!taxRate) {
            // eslint-disable-next-line no-console
            console.error('No tax rate found');
            return;
        }

        const nextItems = formikContext.values.allItems.map(item => {
            if (isInvoiceProductItem(item)) {
                return InvoiceProductItem.fromPlainObject({
                    ...item,
                    taxRate,
                    tax: taxRate.taxPercentage ?? undefined,
                });
            } else if (isInvoiceGenericItem(item)) {
                return {
                    ...item,
                    taxRate,
                    tax: taxRate.taxPercentage ?? undefined,
                };
            }
            return item;
        });

        formikContext.setFieldValue(positionFieldNames.allItems, nextItems);

        setShowTaxRateOverrideModal?.(false);
        setTaxRateForOverride?.(undefined);
    };

    return (
        <PoolModal open={showTaxRateOverrideModal} onClose={() => setShowTaxRateOverrideModal?.(false)}>
            <PoolModalTitle>
                <FormattedMessage id="app.invoices.detail.taxRate.title" />
            </PoolModalTitle>
            <PoolModalContent>
                <Stack gap={2}>
                    <Box sx={{ flex: 1 }}>
                        <TaxRateAutocomplete
                            ref={taxRatesRef}
                            required={false}
                            taxRates={taxRates}
                            onValueChange={taxRate => dispatch(changeRecipientTaxRateId(taxRate?.id ?? null))}
                            value={recipientTaxRateId}
                            useId={true}
                        />
                    </Box>

                    {showBanner && (
                        <PoolBanner onClose={() => setShowBanner(false)} variant="outlined" sx={{ textAlign: 'left' }}>
                            <FormattedMessage id="app.invoices.detail.taxRate.banner" />
                        </PoolBanner>
                    )}
                </Stack>
            </PoolModalContent>
            <PoolModalActions>
                <PoolButton onClick={() => setShowTaxRateOverrideModal?.(false)} variant="outlined">
                    <FormattedMessage id="app.common.close" />
                </PoolButton>
                <PoolButton onClick={handleSetAllTaxRates}>
                    <FormattedMessage id="app.invoices.detail.taxRate.apply" />
                </PoolButton>
            </PoolModalActions>
        </PoolModal>
    );
};
